import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RejectCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview';
import { DeclineCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview/components';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useConfirmJobMutation, useDeclineCandidateMutation } from '~/domain/Jobs';
import { useLoading } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  fireConfirmCandidateConfirmClicked,
  fireConfirmCandidateDeclineClicked,
  fireConfirmCandidateDeclineConfirmed,
} from '~/posthogs/events';
import {
  DECLINE_CANDIDATE,
  SHOW_CONFIRM_CANDIDATE_ANOTHER_JOB_POPUP,
  SHOW_CONFIRM_CANDIDATE_ELIGIBLE_JOBS_POPUP,
  SHOW_MESSAGE,
} from '../../../actions';
import { Button } from '@tempmee/react';
import { isPartialShift } from '../../../commonComponents/CommonFunctions';
import CounterShiftPopup from '../../CounterShiftPopup';
import DeclineCandidatePopup from '../../DeclineCandidatePopup';

const getAdjacentCounterOffer = (allCandidates = [], counterOfferId) => {
  const declinedIndex = allCandidates.findIndex(
    (counterOffer) => counterOffer?.id === counterOfferId,
  );

  if (declinedIndex === -1) {
    return null;
  }

  return allCandidates[declinedIndex + 1] ?? allCandidates[declinedIndex - 1] ?? null;
};

export const ConfirmOrDecline = ({
  candidateId,
  job,
  currentCounter: providedCurrentCounter,
  allCandidates,
  notesForTemp,
  allowNavigation,
  onDeclineConfirmed,
  small = false,
}) => {
  const dispatch = useDispatch();
  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);
  // Owns the confirm-flow UI side effects: the mutation classifies the outcome and
  // reports it here (before it invalidates the job query) so this call site decides
  // which popup/message to dispatch. GOTU-4025 will move these flags to local state.
  const confirmJobMutation = useConfirmJobMutation({
    onOutcome: ({ outcome, response }, variables) => {
      if (outcome.type === 'show_another_job_popup') {
        dispatch({
          type: SHOW_CONFIRM_CANDIDATE_ANOTHER_JOB_POPUP,
          payload: { candidate: variables.candidate, officeId: variables.officeId },
        });
        return;
      }

      if (outcome.type === 'show_eligible_jobs_popup') {
        dispatch({
          type: SHOW_CONFIRM_CANDIDATE_ELIGIBLE_JOBS_POPUP,
          payload: {
            candidate: variables.candidate,
            professionalId: variables.candidate?.id,
            officeId: variables.officeId,
            job: response,
          },
        });
        return;
      }

      if (outcome.type === 'onsite_confirmed') {
        const m2mOnsiteInterviewMessage =
          `Good news, ${variables.candidate?.full_name} has been confirmed ` +
          `for your onsite interview on ${outcome.localDate}.`;

        dispatch({
          type: SHOW_MESSAGE,
          payload: isEnablePermanentJobM2M
            ? {
                title: 'Interview Confirmed!',
                message: m2mOnsiteInterviewMessage,
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              }
            : {
                title: 'Good News!',
                message: 'Your onsite interview has successfully been filled.',
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              },
        });
        return;
      }

      if (outcome.type === 'confirmed_no_followup') {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Good News!',
            message: 'Your shift has successfully been filled.',
            route: outcome.route,
            isSuccess: true,
          },
        });
      }
    },
  });
  const declineCandidateMutation = useDeclineCandidateMutation();

  const [declineCandidatePopup, setDeclineCandidatePopup] = useState(false);
  const [counterShiftPopup, setCounterShiftPopup] = useState(false);
  const currentCounter =
    providedCurrentCounter ??
    allCandidates?.find((counter) => counter?.candidate?.id === candidateId);
  const isCounterHours = isPartialShift(currentCounter);

  const { isLoading: isDeclineCandidateLoading } = useLoading([DECLINE_CANDIDATE]);

  const isOnsiteInterview = job?.isOnsiteInterview;

  const declineCandidatePressed = () => {
    fireConfirmCandidateDeclineClicked({ shiftId: job.id });
    setDeclineCandidatePopup(true);
  };

  const handleAcceptClicked = () => {
    setCounterShiftPopup(false);
    confirmOffer();
  };

  const handleCancelClicked = () => {
    setCounterShiftPopup(false);
  };

  const handleConfirmDecline = (reason = '') => {
    fireConfirmCandidateDeclineConfirmed({ shiftId: job.id });
    onDeclineConfirmed?.(allCandidates?.length > 1);
    const declinePayload = {
      jobId: job.id,
      counterOfferId: currentCounter?.id,
      cancellation_reason: reason,
      candidateId: currentCounter?.candidate?.id,
    };

    const nextCandidateId = !isOnsiteInterview
      ? getAdjacentCounterOffer(allCandidates, currentCounter?.id)?.candidate?.id
      : null;

    if (nextCandidateId) {
      declinePayload.nextCandidateId = nextCandidateId;
    }

    declineCandidateMutation.mutate(declinePayload);
    setDeclineCandidatePopup(false);
  };

  const confirmOffer = () => {
    fireConfirmCandidateConfirmClicked({ shiftId: job.id });
    allowNavigation();

    confirmJobMutation.mutate({
      jobId: job.id,
      data: { notes: notesForTemp },
      counterOfferId: currentCounter?.id,
      candidate: currentCounter?.candidate,
      officeId: job?.offer_owner?.id,
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      <Button
        variant="secondary"
        size={small ? 'xs' : 'md'}
        style={{ marginRight: !small ? 20 : 12 }}
        onClick={declineCandidatePressed}
      >
        Decline
      </Button>
      <Button
        size={small ? 'xs' : 'md'}
        onClick={() => {
          if (isCounterHours) {
            setCounterShiftPopup(true);
          } else {
            confirmOffer();
          }
        }}
      >
        Confirm
      </Button>

      {declineCandidatePopup && (
        <>
          {isOnsiteInterview ? (
            <>
              {isEnablePermanentJobM2M ? (
                <DeclineCandidateConfirmationPopup
                  isLoading={isDeclineCandidateLoading || declineCandidateMutation.isPending}
                  jobLocalDate={job.local_date}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              ) : (
                <RejectCandidateConfirmationPopup
                  isLoading={isDeclineCandidateLoading || declineCandidateMutation.isPending}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  useDeclineTerms
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              )}
            </>
          ) : (
            <DeclineCandidatePopup
              declineBtnPressed={handleConfirmDecline}
              goBackPressed={() => setDeclineCandidatePopup(false)}
            />
          )}
        </>
      )}

      {counterShiftPopup ? (
        <CounterShiftPopup
          job={job}
          currentCounter={currentCounter}
          acceptBtnPressed={handleAcceptClicked}
          cancelBtnPressed={handleCancelClicked}
        />
      ) : null}
    </div>
  );
};
